import type {
    TMonthsBasedDatePickerOption,
    TMonthsBasedDatePickerDataRange,
    TMonthsBasedDatePickerDataDate,
} from "./MonthsBasedDatePicker";

type DateParts = { month: number; year: number };

export const parseMonthYear = (dateString: string): DateParts => {
    const [month, year] = dateString.split("/").map(Number);
    if (month === undefined || year === undefined) {
        throw new Error("Invalid date string");
    }
    return { month, year };
};

const formatMonthYear = ({ month, year }: DateParts): string => {
    return `${month.toString().padStart(2, "0")}/${year}`;
};

const compareDates = (date1: string, date2: string): number => {
    const d1 = parseMonthYear(date1);
    const d2 = parseMonthYear(date2);
    return d1.year !== d2.year ? d1.year - d2.year : d1.month - d2.month;
};

const getQuarterInfo = (month: number) => {
    const quarterNumber = Math.ceil(month / 3);
    const startMonth = (quarterNumber - 1) * 3 + 1;
    const endMonth = quarterNumber * 3;
    return { quarterNumber, startMonth, endMonth };
};

const getMonthsInQuarterBeforeEndDate = (
    currentDate: string,
    endDate: string,
): number => {
    const current = parseMonthYear(currentDate);
    const end = parseMonthYear(endDate);

    if (current.year === end.year) {
        return Math.min(current.month - end.month + 1, 3);
    }
    return 3;
};

const createQuarterOption = (
    year: number,
    quarterInfo: ReturnType<typeof getQuarterInfo>,
    floatedMonths: number,
): TMonthsBasedDatePickerOption<
    TMonthsBasedDatePickerDataRange | TMonthsBasedDatePickerDataDate
> => {
    return {
        item: {
            data: {
                type: "range",
                start: formatMonthYear({ month: quarterInfo.startMonth, year }),
                end: formatMonthYear({ month: quarterInfo.endMonth, year }),
            },
            label: `Q${quarterInfo.quarterNumber}`,
            id: `Q${quarterInfo.quarterNumber}-${year}`,
        },
        floated: floatedMonths,
    };
};

const createMonthOption = (
    date: string,
    highlightedEndDates: string[],
): TMonthsBasedDatePickerOption<
    TMonthsBasedDatePickerDataRange | TMonthsBasedDatePickerDataDate
> => {
    const isHighlighted = highlightedEndDates.some(dateString => {
        return dateString === date;
    });

    return {
        item: {
            data: { type: "date", value: date },
            label: date,
            id: date,
        },
        icons: isHighlighted
            ? {
                  trailingIcon: "check",
              }
            : undefined,
    };
};
export const generateMonthsOptionsWithQuartersAsFloatingItems = (
    currentDate: string,
    endDate: string,
    highlightedEndDates: string[] = [],
): TMonthsBasedDatePickerOption<
    TMonthsBasedDatePickerDataRange | TMonthsBasedDatePickerDataDate
>[] => {
    if (compareDates(currentDate, endDate) < 0) {
        const { month, year } = parseMonthYear(currentDate);
        const quarterInfo = getQuarterInfo(month);
        return [
            createQuarterOption(year, quarterInfo, 1),
            createMonthOption(currentDate, highlightedEndDates),
        ];
    }

    const generateOptionsForDate = (date: {
        month: number;
        year: number;
    }): TMonthsBasedDatePickerOption<
        TMonthsBasedDatePickerDataRange | TMonthsBasedDatePickerDataDate
    >[] => {
        const formattedDate = formatMonthYear(date);

        if (compareDates(formattedDate, endDate) < 0) {
            return [];
        }

        const quarterInfo = getQuarterInfo(date.month);
        const isQuarterEnd = date.month === quarterInfo.endMonth;
        const isCurrentDate = formattedDate === currentDate;

        const nextDate = {
            month: date.month === 1 ? 12 : date.month - 1,
            year: date.month === 1 ? date.year - 1 : date.year,
        };

        const quarterOption =
            isQuarterEnd || isCurrentDate
                ? [
                      createQuarterOption(
                          date.year,
                          quarterInfo,
                          isCurrentDate
                              ? date.month - quarterInfo.startMonth + 1
                              : getMonthsInQuarterBeforeEndDate(
                                    formattedDate,
                                    endDate,
                                ),
                      ),
                  ]
                : [];

        const monthOption = [
            createMonthOption(formattedDate, highlightedEndDates),
        ];

        return [
            ...quarterOption,
            ...monthOption,
            ...generateOptionsForDate(nextDate),
        ];
    };

    return generateOptionsForDate(parseMonthYear(currentDate));
};
